import { cn } from "@tellescope/ui";
import { MouseEvent, forwardRef, useImperativeHandle, useRef, useState } from "react";

import { anchorElementToMarkup } from "../editor/anchorLinks";
import { TEMPLATE_TOKEN_ATTR } from "../editor/TemplateTokenNode";
import { TokenCollectorPopover } from "../palette/TokenCollectorPopover";
import { TokenPreview } from "../palette/TokenPreview";
import type { CollectorAnchor } from "../palette/useTokenPalette";
import { extractTokenParams } from "../tokens/recognize";
import type { TokenDefinition } from "../tokens/types";
import { getTokenLabel } from "../tokens/tokenLabel";
import { htmlToSegments } from "./htmlTokenSegments";

/** An open edit session on a specific pill span. */
interface HtmlPillEdit {
  definition: TokenDefinition;
  values: Record<string, string>;
  anchor: CollectorAnchor;
  span: HTMLElement;
}

/** Center of an element in viewport coordinates. */
const centerOf = (element: HTMLElement): CollectorAnchor => {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

/** The nearest token pill for an event target, if any. */
const pillFrom = (target: EventTarget | null): HTMLElement | null =>
  target instanceof Element ? target.closest<HTMLElement>(`[${TEMPLATE_TOKEN_ATTR}]`) : null;

/** Shared pill styling, used for both the React-rendered chips and imperatively-inserted ones. */
const CHIP_CLASS =
  "mx-0.5 inline-flex select-none items-center rounded-md bg-muted px-1.5 py-0.5 align-baseline font-sans text-xs text-muted-foreground";

export interface HtmlTokenFieldHandle {
  /** Insert a token pill at the caret (or append) and emit the updated source. */
  insertToken: (raw: string) => void;
  /** Insert plain text at the caret (or append) and emit the updated source. */
  insertText: (text: string) => void;
}

export interface HtmlTokenFieldProps {
  id?: string;
  html?: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  /** When true, keep literal `<a>` tags in the serialized source; otherwise emit `{url}[text]`. */
  preserveAnchorTags?: boolean;
  /** Extra classes for the editable surface — e.g. to override the default monospace HTML font. */
  className?: string;
}

/**
 * Serialize the editable DOM back to the raw HTML source: pills → their raw token, `<br>` → newline,
 * and (unless `preserveAnchorTags`) each `<a>` → its `{url}[text]` link markup so stored links carry
 * click tracking and a plaintext form. A preserved anchor is emitted as its literal `outerHTML`.
 */
const serialize = (element: HTMLElement, preserveAnchorTags: boolean): string => {
  let out = "";
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.nodeValue ?? "";
    } else if (node instanceof HTMLElement) {
      const raw = node.getAttribute(TEMPLATE_TOKEN_ATTR);
      if (raw !== null) out += raw;
      else if (node.tagName === "BR") out += "\n";
      else if (node instanceof HTMLAnchorElement) {
        const inner = serialize(node, preserveAnchorTags);
        out += preserveAnchorTags
          ? `<a href="${node.getAttribute("href") ?? ""}">${inner}</a>`
          : anchorElementToMarkup(node, inner);
      } else out += serialize(node, preserveAnchorTags);
    }
  });
  return out;
};

const createChip = (raw: string): HTMLSpanElement => {
  const span = document.createElement("span");
  span.setAttribute(TEMPLATE_TOKEN_ATTR, raw);
  span.contentEditable = "false";
  span.title = raw;
  span.className = CHIP_CLASS;
  span.textContent = getTokenLabel(raw);
  return span;
};

/**
 * Raw-HTML source editor that renders templated tokens as pill badges. It is intentionally
 * uncontrolled: the initial source is rendered once (text runs + non-editable token pills), the DOM
 * is the source of truth while mounted, and edits/insertions emit the re-serialized source. Mode
 * switching unmounts/remounts it, reseeding from the latest source.
 */
export const HtmlTokenField = forwardRef<HtmlTokenFieldHandle, HtmlTokenFieldProps>(
  ({ id, html, onChange, disabled, preserveAnchorTags = false, className }, ref) => {
    const editableRef = useRef<HTMLDivElement>(null);
    // Rendered once; key each segment by its (stable, unique) start offset in the source.
    const [initialSegments] = useState(() => {
      let offset = 0;
      return htmlToSegments(html ?? "").map((segment) => {
        const key = offset;
        offset += segment.value.length;
        return { ...segment, key };
      });
    });

    const [editing, setEditing] = useState<HtmlPillEdit | null>(null);
    const [hover, setHover] = useState<{ raw: string; anchor: CollectorAnchor } | null>(null);

    const emit = () => {
      if (editableRef.current) onChange(serialize(editableRef.current, preserveAnchorTags));
    };

    // Pills are a mix of React-rendered and imperatively-created DOM, so click/hover are handled by
    // delegation on the container. A parameterized pill opens the edit collector / shows a preview.
    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      const span = pillFrom(event.target);
      const extracted = span && extractTokenParams(span.getAttribute(TEMPLATE_TOKEN_ATTR) ?? "");
      if (!span || !extracted) return;
      event.preventDefault();
      setHover(null);
      setEditing({ definition: extracted.definition, values: extracted.values, anchor: centerOf(span), span });
    };

    const handleMouseOver = (event: MouseEvent<HTMLDivElement>) => {
      const span = pillFrom(event.target);
      const raw = span?.getAttribute(TEMPLATE_TOKEN_ATTR) ?? "";
      if (span && extractTokenParams(raw)) setHover({ raw, anchor: centerOf(span) });
      else setHover(null);
    };

    const submitEdit = (params: Record<string, string>) => {
      if (!editing) return;
      const newRaw = editing.definition.build(params);
      editing.span.setAttribute(TEMPLATE_TOKEN_ATTR, newRaw);
      editing.span.title = newRaw;
      editing.span.textContent = getTokenLabel(newRaw);
      emit();
      setEditing(null);
    };

    const insertNode = (node: Node) => {
      const element = editableRef.current;
      if (!element) return;
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && element.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        element.appendChild(node);
      }
      emit();
    };

    useImperativeHandle(ref, () => ({
      insertToken: (raw: string) => insertNode(createChip(raw)),
      insertText: (text: string) => insertNode(document.createTextNode(text)),
    }));

    return (
      <>
        <div
          id={id}
          ref={editableRef}
          role="textbox"
          aria-label="Edit HTML"
          aria-multiline
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onClick={handleClick}
          onMouseOver={handleMouseOver}
          onMouseOut={() => setHover(null)}
          className={cn(
            "min-h-48 flex-1 overflow-auto whitespace-pre-wrap p-2 font-mono text-sm outline-none",
            className
          )}
        >
          {initialSegments.map((segment) =>
            segment.type === "text" ? (
              segment.value
            ) : (
              <span
                key={segment.key}
                {...{ [TEMPLATE_TOKEN_ATTR]: segment.value }}
                contentEditable={false}
                title={segment.value}
                className={cn(CHIP_CLASS, extractTokenParams(segment.value) && "cursor-pointer")}
              >
                {getTokenLabel(segment.value)}
              </span>
            )
          )}
        </div>

        {/* Hover preview: a floating box anchored at the hovered pill (hidden while editing). */}
        {hover && !editing && (
          <div
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full rounded-md bg-primary px-3 py-1.5 text-primary-foreground shadow-md"
            style={{ left: hover.anchor.x, top: hover.anchor.y - 8 }}
          >
            <TokenPreview raw={hover.raw} />
          </div>
        )}

        <TokenCollectorPopover
          definition={editing?.definition ?? null}
          initialValues={editing?.values}
          anchor={editing?.anchor ?? null}
          onSubmit={submitEdit}
          onClose={() => setEditing(null)}
        />
      </>
    );
  }
);

HtmlTokenField.displayName = "HtmlTokenField";
